import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { type RequestPreview, useCreateRequest, useRequestPreview } from '../api/queries';

interface Props {
  appName: string | null;
  onClose: () => void;
}

export function RequestAccessDialog({ appName, onClose }: Props) {
  const [roles, setRoles] = useState('');
  const [justification, setJustification] = useState('');
  const [result, setResult] = useState<(RequestPreview & { id: string; sent: boolean }) | null>(
    null,
  );
  const { data: preview, isLoading } = useRequestPreview(appName);
  const createRequest = useCreateRequest();
  const { enqueueSnackbar } = useSnackbar();

  if (!appName) return null;

  const submit = async () => {
    try {
      // Fold the roles/access ask + justification into one body (no schema
      // change — the backend composes the email from `justification`).
      const combined = [roles.trim() && `Access needed: ${roles.trim()}`, justification.trim()]
        .filter(Boolean)
        .join('\n\n');
      const r = await createRequest.mutateAsync({
        app_name: appName,
        justification: combined || null,
      });
      setResult(r);
      // Backend sends the email server-side AS the user when possible. Only
      // open the Outlook draft when it didn't (sent=false) — the success panel
      // also has a reliable fallback link for pop-up blockers / slow Outlook.
      if (!r.sent) window.open(r.compose_url, '_blank', 'noopener');
    } catch (err) {
      enqueueSnackbar(`Request failed: ${err instanceof Error ? err.message : err}`, {
        variant: 'error',
      });
    }
  };

  const close = () => {
    setResult(null);
    setRoles('');
    setJustification('');
    onClose();
  };

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Request access — {appName}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : result ? (
          <Stack spacing={2} sx={{ pt: 1, alignItems: 'flex-start' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'success.main' }}>
              <CheckCircleIcon fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {result.sent ? 'Request sent' : 'Request logged'}
              </Typography>
            </Stack>
            {result.sent ? (
              <Typography variant="body2" color="text.secondary">
                Your request for <strong>{appName}</strong> was emailed to{' '}
                <strong>{result.to}</strong> from your account. IT will follow up by reply.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Your request for <strong>{appName}</strong> is recorded. A pre-filled email to{' '}
                  <strong>{result.to}</strong> should have opened in a new tab — or use the button
                  below. Nothing is sent automatically until you hit Send.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={result.compose_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in Outlook
                </Button>
              </>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              We&rsquo;ll log your request and open a pre-filled email to{' '}
              <strong>{preview?.to}</strong>. Nothing sends until you hit Send in Outlook.
            </Typography>
            <TextField
              label="What roles or access level do you need?"
              placeholder="e.g. Editor on the Finance workspace · read-only · admin"
              multiline
              minRows={2}
              autoFocus
              value={roles}
              onChange={(e) => setRoles(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
            <TextField
              label="Why do you need it? (optional)"
              multiline
              minRows={2}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
              }}
              slotProps={{ htmlInput: { maxLength: 5000 } }}
            />
            {createRequest.isError && <Alert severity="error">{String(createRequest.error)}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button variant="contained" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={createRequest.isPending}>
              {createRequest.isPending ? 'Submitting…' : 'Submit request'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
